// First Created - 07 Oct 2026
// Author - Atul Pandey
// Purpose - API slice for Org Trail Session Q&A Assignments


import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'


import {
    apiSlice,
} from '@/app/api/apiSlice'



const orgTrailSessionQNAAssignmentsAdapter =
    createEntityAdapter({})


const initialState =
    orgTrailSessionQNAAssignmentsAdapter
        .getInitialState()



export const orgTrailSessionQNAAssignmentsApiSlice =
    apiSlice.injectEndpoints({

        endpoints:
            builder => ({


                /*
                |--------------------------------------------------------------------------
                | 01 - Get Q&A Assignments
                |--------------------------------------------------------------------------
                */

                getOrgTrailSessionQNAAssignments:
                    builder.query({

                        query: ({
                            orgId = '',
                            trailId = '',
                            sessionId = '',
                            cohortId = '',
                            active = '',
                        } = {}) => ({

                            url:
                                '/orgTrailSessionQNAAssignments',

                            params: {

                                ...(orgId
                                    ? {
                                        orgId
                                    }
                                    : {}),


                                ...(trailId
                                    ? {
                                        trailId
                                    }
                                    : {}),


                                ...(sessionId
                                    ? {
                                        sessionId
                                    }
                                    : {}),


                                ...(cohortId
                                    ? {
                                        cohortId
                                    }
                                    : {}),


                                ...(active !== ''
                                    ? {
                                        active
                                    }
                                    : {}),

                            },


                            validateStatus: (
                                response,
                                result
                            ) => {

                                return (
                                    response.status ===
                                        200 &&
                                    !result?.isError
                                )
                            },

                        }),



                        /*
                        |--------------------------------------------------------------------------
                        | Normalize Backend Array
                        |--------------------------------------------------------------------------
                        */

                        transformResponse:
                            responseData => {

                                const assignments =
                                    Array.isArray(
                                        responseData
                                    )
                                        ?
                                        responseData
                                        :
                                        []


                                const loadedAssignments =
                                    assignments.map(
                                        assignment => ({

                                            ...assignment,

                                            id:
                                                assignment
                                                    ._id,

                                        })
                                    )


                                return orgTrailSessionQNAAssignmentsAdapter
                                    .setAll(
                                        initialState,
                                        loadedAssignments
                                    )
                            },



                        providesTags: (
                            result
                        ) => {

                            if (
                                result?.ids
                            ) {

                                return [

                                    {
                                        type:
                                            'OrgTrailSessionQNAAssignment',

                                        id:
                                            'LIST',
                                    },


                                    ...result.ids.map(
                                        id => ({

                                            type:
                                                'OrgTrailSessionQNAAssignment',

                                            id,

                                        })
                                    ),

                                ]
                            }


                            return [

                                {
                                    type:
                                        'OrgTrailSessionQNAAssignment',

                                    id:
                                        'LIST',
                                },

                            ]
                        },

                    }),



                /*
                |--------------------------------------------------------------------------
                | 02 - Create Q&A Assignment
                |--------------------------------------------------------------------------
                */

                createOrgTrailSessionQNAAssignment:
                    builder.mutation({

                        query:
                            initialAssignment => ({

                                url:
                                    '/orgTrailSessionQNAAssignments',

                                method:
                                    'POST',

                                body: {
                                    ...initialAssignment,
                                },

                            }),


                        invalidatesTags: [

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    'USER_SESSION',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 03 - Update Q&A Assignment
                |--------------------------------------------------------------------------
                */

                updateOrgTrailSessionQNAAssignment:
                    builder.mutation({

                        query:
                            assignment => ({

                                url:
                                    '/orgTrailSessionQNAAssignments',

                                method:
                                    'PATCH',

                                body: {
                                    ...assignment,
                                },

                            }),


                        invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    arg.id,
                            },

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    'USER_SESSION',
                            },

                        ],

                    }),



                /*
                |--------------------------------------------------------------------------
                | 04 - Delete Q&A Assignment
                |--------------------------------------------------------------------------
                */

                deleteOrgTrailSessionQNAAssignment:
                    builder.mutation({

                        query: ({
                            id,
                        }) => ({

                            url:
                                '/orgTrailSessionQNAAssignments',

                            method:
                                'DELETE',

                            body: {
                                id,
                            },

                        }),


                        invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    arg.id,
                            },

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    'LIST',
                            },

                            {
                                type:
                                    'OrgTrailSessionQNAAssignment',

                                id:
                                    'USER_SESSION',
                            },

                        ],

                    }),

            }),

    })



/*
|--------------------------------------------------------------------------
| 05 - Hooks
|--------------------------------------------------------------------------
*/

export const {

    useGetOrgTrailSessionQNAAssignmentsQuery,

    useLazyGetOrgTrailSessionQNAAssignmentsQuery,

    useCreateOrgTrailSessionQNAAssignmentMutation,

    useUpdateOrgTrailSessionQNAAssignmentMutation,

    useDeleteOrgTrailSessionQNAAssignmentMutation,

} =
    orgTrailSessionQNAAssignmentsApiSlice



/*
|--------------------------------------------------------------------------
| 06 - Base Selector
|--------------------------------------------------------------------------
*/

export const selectOrgTrailSessionQNAAssignmentsResult =

    orgTrailSessionQNAAssignmentsApiSlice
        .endpoints
        .getOrgTrailSessionQNAAssignments
        .select()



/*
|--------------------------------------------------------------------------
| 07 - Assignment Data Selector
|--------------------------------------------------------------------------
*/

const selectOrgTrailSessionQNAAssignmentsData =

    createSelector(

        selectOrgTrailSessionQNAAssignmentsResult,

        assignmentResult =>
            assignmentResult.data

    )



/*
|--------------------------------------------------------------------------
| 08 - Entity Selectors
|--------------------------------------------------------------------------
*/

export const {

    selectAll:
        selectAllOrgTrailSessionQNAAssignments,

    selectById:
        selectOrgTrailSessionQNAAssignmentById,

    selectIds:
        selectOrgTrailSessionQNAAssignmentIds,

} =

    orgTrailSessionQNAAssignmentsAdapter
        .getSelectors(

            state =>

                selectOrgTrailSessionQNAAssignmentsData(
                    state
                ) ??

                initialState

        )